
import React, { useEffect, useRef } from "react";

function QuillEditor({
    value = "",
    onChange,
    placeholder = "",
}) {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Initialize Quill.
    useEffect(() => {
        const editorElement = editorRef.current;

        if (!editorElement || !window.Quill) {
            console.error("Quill JavaScript is not loaded.");
            return;
        }

        // Avoid initializing the same element twice.
        if (quillRef.current) {
            return;
        }

        // Remove a leftover toolbar belonging to this editor.
        const previousToolbar = editorElement.previousElementSibling;

        if (previousToolbar?.classList.contains("ql-toolbar")) {
            previousToolbar.remove();
        }

        editorElement.innerHTML = "";
        editorElement.classList.remove("ql-container", "ql-snow");

        const quill = new window.Quill(editorElement, {
            theme: "snow",
            placeholder,
            modules: {
                toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link"],
                    ["clean"],
                ],
            },
        });

        quillRef.current = quill;

        const handleTextChange = (delta, oldDelta, source) => {
            if (source === "user") {
                onChangeRef.current?.(quill.root.innerHTML);
            }
        };

        quill.on("text-change", handleTextChange);

        return () => {
            quill.off("text-change", handleTextChange);

            // Remove the toolbar created by this Quill instance.
            const toolbar = editorElement.previousElementSibling;

            if (toolbar?.classList.contains("ql-toolbar")) {
                toolbar.remove();
            }

            // Clear the old Quill instance's DOM.
            editorElement.innerHTML = "";
            editorElement.classList.remove("ql-container", "ql-snow");

            quillRef.current = null;
        };
    }, [placeholder]);

    // Sync incoming HTML into Quill.
    useEffect(() => {
        const quill = quillRef.current;

        if (!quill) return;

        const html = value ?? "";

        if (quill.root.innerHTML === html) return;

        if (!html.trim()) {
            quill.setText("", "silent");
            return;
        }

        quill.clipboard.dangerouslyPasteHTML(html, "silent");
    }, [value]);

    return <div ref={editorRef} />;
}

export default QuillEditor;